import type { Id, Scene } from "@supermanus/document-model";
import { formatDuration, parseItemNumber, totalSceneDuration } from "@supermanus/document-model";
import {
  Fragment,
  useEffect,
  useState,
  type MouseEvent as ReactMouseEvent,
  type Ref,
} from "react";
import { DocumentHead } from "./DocumentHead";
import { DurationField } from "./DurationField";
import { handleFieldEnterKey } from "./fieldKeyboard";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";
import { reorderDropClass, useSceneReorder } from "./useSceneReorder";

export interface TimingViewProps {
  scenes: Scene[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  defaultSceneHeaderBg: string;
  onDurationChange: (sceneId: Id, durationSeconds: number) => void;
  onMetaChange?: (
    sceneId: Id,
    patch: Partial<Pick<Scene, "title" | "number">>,
  ) => void;
  onMoveScene?: (sceneId: Id, beforeSceneId: Id | null) => void;
  onSceneHeaderContextMenu?: (
    sceneId: Id,
    event: ReactMouseEvent,
  ) => void;
  onOpenScene?: (sceneId: Id) => void;
  /** Extra class on each scene row (e.g. director own/program). */
  sceneRowClass?: (sceneId: Id) => string | undefined;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
}

/** Borderless number field — same weight/size as surrounding table text. */
function TimingSceneNumber({
  value,
  ariaLabel,
  onChange,
}: {
  value: string | number;
  ariaLabel: string;
  onChange: (number: string) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  function commit() {
    const parsed = parseItemNumber(draft);
    if (parsed) {
      if (parsed !== String(value)) onChange(parsed);
      else setDraft(String(value));
    } else {
      setDraft(String(value));
    }
  }

  return (
    <input
      className="sm-timing-num"
      value={draft}
      size={Math.max(1, draft.length)}
      aria-label={ariaLabel}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        handleFieldEnterKey(e);
        if (e.key === "Escape") {
          e.preventDefault();
          setDraft(String(value));
          e.currentTarget.blur();
        }
      }}
    />
  );
}

export function TimingView({
  scenes,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  defaultSceneHeaderBg,
  onDurationChange,
  onMetaChange,
  onMoveScene,
  onSceneHeaderContextMenu,
  onOpenScene,
  sceneRowClass,
  showHours = true,
  showFrames = false,
  fps = 25,
}: TimingViewProps) {
  const [listEditing, setListEditing] = useState(false);
  const total = totalSceneDuration(scenes);
  const fmt = { showHours, showFrames, fps };
  const numDigits = Math.min(
    7,
    Math.max(3, ...scenes.map((s) => String(s.number).length)),
  );
  const editingTitles = listEditing && !readOnly;
  const { listRef, dragSceneId, dropBeforeSceneId, beginSceneDrag } =
    useSceneReorder({
      readOnly: readOnly || !onMoveScene,
      onMoveScene: onMoveScene ?? (() => undefined),
    });
  const canEditNumber = !readOnly && Boolean(onMetaChange);
  const canEditTitle = editingTitles && Boolean(onMetaChange);
  const showGrips = !readOnly && Boolean(onMoveScene);

  return (
    <div className="sm-paper sm-paper-a4 sm-view-timing">
      {!readOnly ? (
        <div className="sm-paper-tools sm-no-print">
          <button
            type="button"
            className={`sm-status-edit${listEditing ? " is-active" : ""}`}
            title={
              listEditing
                ? "Afslut redigering af scenetitler"
                : "Rediger scenetitler"
            }
            aria-pressed={listEditing}
            aria-label={
              listEditing ? "Afslut redigering" : "Rediger scenetitler"
            }
            onClick={() => setListEditing((on) => !on)}
          >
            ✎
          </button>
        </div>
      ) : null}
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="timing"
      />
      <table
        ref={listRef as Ref<HTMLTableElement>}
        className={`sm-timing-table${dragSceneId ? " is-reordering" : ""}${
          editingTitles ? " is-editing-titles" : ""
        }`}
        style={{ ["--sm-timing-num-ch" as string]: String(numDigits) }}
      >
        <thead>
          <tr>
            {showGrips ? (
              <th className="sm-timing-grip-col" aria-label="Flyt" />
            ) : null}
            <th className="sm-timing-num-col">#</th>
            <th className="sm-timing-title-col">Titel</th>
            <th className="sm-timing-dur">Varighed</th>
          </tr>
        </thead>
        <tbody>
          {scenes.map((scene, i) => {
            const dropClass = reorderDropClass(
              dragSceneId,
              dropBeforeSceneId,
              scene.id,
              i === scenes.length - 1,
            );
            const directorClass = sceneRowClass?.(scene.id) ?? "";
            return (
              <Fragment key={scene.id}>
                <tr
                  className={
                    `${dragSceneId === scene.id ? "is-dragging" : ""}${dropClass}${directorClass ? ` ${directorClass}` : ""}`.trim() ||
                    undefined
                  }
                  style={{
                    backgroundColor: sceneHeaderBg(
                      scene.headerBackground,
                      defaultSceneHeaderBg,
                    ),
                  }}
                  data-scene-id={scene.id}
                  onClick={() => {
                    if (editingTitles || !onOpenScene) return;
                    onOpenScene(scene.id);
                  }}
                  onContextMenu={(e) => {
                    if (readOnly || !onSceneHeaderContextMenu) return;
                    e.preventDefault();
                    e.stopPropagation();
                    onSceneHeaderContextMenu(scene.id, e);
                  }}
                  title={
                    !editingTitles && onOpenScene
                      ? "Åbn scene i manus"
                      : readOnly
                        ? undefined
                        : "Højreklik for baggrundsfarve, dupliker, flyt, opdel eller slet"
                  }
                >
                  {showGrips ? (
                    <td className="sm-timing-grip-col">
                      <button
                        type="button"
                        className="sm-scene-grip"
                        aria-label="Flyt scene"
                        title="Træk for at flytte scene"
                        onPointerDown={(e) => beginSceneDrag(scene.id, e)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        ⋮⋮
                      </button>
                    </td>
                  ) : null}
                  <td
                    className="sm-timing-num-col"
                    onClick={
                      canEditNumber ? (e) => e.stopPropagation() : undefined
                    }
                    onMouseDown={
                      canEditNumber ? (e) => e.stopPropagation() : undefined
                    }
                  >
                    {canEditNumber ? (
                      <TimingSceneNumber
                        value={scene.number}
                        ariaLabel={`Nummer for scene ${scene.number}`}
                        onChange={(number) =>
                          onMetaChange?.(scene.id, { number })
                        }
                      />
                    ) : (
                      scene.number
                    )}
                  </td>
                  <td className="sm-timing-title-col">
                    {canEditTitle ? (
                      <input
                        className="sm-timing-title"
                        value={scene.title}
                        placeholder="Unavngiven scene"
                        aria-label={`Titel for scene ${scene.number}`}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          onMetaChange?.(scene.id, { title: e.target.value })
                        }
                        onKeyDown={handleFieldEnterKey}
                      />
                    ) : (
                      scene.title || "Unavngiven scene"
                    )}
                  </td>
                  <td className="sm-timing-dur">
                    <DurationField
                      valueSeconds={scene.durationSeconds}
                      onChange={(seconds) =>
                        onDurationChange(scene.id, seconds)
                      }
                      readOnly={readOnly}
                      showHours={showHours}
                      showFrames={showFrames}
                      fps={fps}
                      ariaLabel={`Varighed for scene ${scene.number}`}
                    />
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={showGrips ? 3 : 2}>I alt</td>
            <td className="sm-timing-dur">
              <span className="sm-timing-total">
                {formatDuration(total, fmt)}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
